import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
// Using simple SVG icons instead of lucide-react
import { useCart } from '../../contexts/CartContext';
import { useToast } from '../../contexts/ToastContext';
import { useMenuItemCustomizations } from '../../hooks/useMenuCustomizations';
import { generateCartItemId, isRestaurantOpenAt } from '../../services/foodOrderingApi';
import { MenuItemCustomizationForm, MenuItemDisplayData, ObjectId } from '../../types';

interface MenuItemModalProps {
  item: MenuItemDisplayData | null;
  isOpen: boolean;
  onClose: () => void;
  restaurant?: any; // Restaurant data to set in cart
}

/**
 * MenuItemModal Component
 *
 * Modal for customizing menu items before adding to cart.
 * Handles:
 * - Single and multiple selection customizations
 * - Quantity selection
 * - Special instructions/chef notes
 * - Price calculation with customizations
 */
const MenuItemModal: React.FC<MenuItemModalProps> = ({ item, isOpen, onClose, restaurant }) => {
  const { actions } = useCart();
  const { success } = useToast();
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Fetch customizations only when modal is open and item is selected
  const { data: customizations, isLoading: isLoadingCustomizations } = useMenuItemCustomizations(
    isOpen && item ? item.id : null
  );

  // Merge fetched customizations with item data
  const itemWithCustomizations =
    item && customizations
      ? {
          ...item,
          customizations: customizations,
        }
      : item;

  // Form state
  const [formData, setFormData] = useState<MenuItemCustomizationForm>({
    quantity: 1,
    customizations: {},
    specialInstructions: '',
  });

  // Reset form when item changes
  useEffect(() => {
    if (item) {
      setFormData({
        quantity: 1,
        customizations: {},
        specialInstructions: '',
      });
    }
  }, [item]);

  // Focus management
  useEffect(() => {
    if (isOpen && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Trap focus within modal
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  };

  if (!item) return null;

  // Use item with fetched customizations
  const displayItem = itemWithCustomizations || item;

  // Calculate total price including customizations
  const calculateTotalPrice = () => {
    let customizationTotal = 0;

    Object.entries(formData.customizations).forEach(([customizationId, selectedOptionIds]) => {
      const customization = displayItem.customizations.find((c: any) => c.id === customizationId);
      if (customization?.menu_customisation_items) {
        selectedOptionIds.forEach((optionId) => {
          const option = customization.menu_customisation_items!.find(
            (opt: any) => opt.id === optionId
          );
          if (option) {
            customizationTotal += option.price;
          }
        });
      }
    });

    return (displayItem.price + customizationTotal) * formData.quantity;
  };

  // Handle customization selection
  const handleCustomizationChange = (
    customizationId: ObjectId,
    optionId: ObjectId,
    isSelected: boolean
  ) => {
    const customization = displayItem.customizations.find((c) => c.id === customizationId);
    if (!customization) return;

    setFormData((prev) => {
      const currentSelections = prev.customizations[customizationId] || [];

      if (customization.is_multichoice) {
        // Multiple selection allowed
        let newSelections: ObjectId[];

        if (isSelected) {
          // Check max selection limit
          if (
            customization.max_selection_number &&
            currentSelections.length >= customization.max_selection_number
          ) {
            return prev; // Don't add if at max limit
          }
          newSelections = [...currentSelections, optionId];
        } else {
          // Allow deselection for multi-choice
          // If required and this is the last selection, prevent deselection
          if (customization.is_required && currentSelections.length === 1) {
            return prev; // Don't allow deselecting the last required option
          }
          newSelections = currentSelections.filter((id) => id !== optionId);
        }

        return {
          ...prev,
          customizations: {
            ...prev.customizations,
            [customizationId]: newSelections,
          },
        };
      } else {
        // Single selection only (radio buttons)
        // For required single-choice, clicking the same option should not deselect it
        // For optional single-choice, allow deselection
        if (!isSelected && currentSelections.includes(optionId)) {
          // User clicked the already selected radio button
          if (customization.is_required) {
            // Required: keep it selected
            return prev;
          } else {
            // Optional: allow deselection
            return {
              ...prev,
              customizations: {
                ...prev.customizations,
                [customizationId]: [],
              },
            };
          }
        }

        return {
          ...prev,
          customizations: {
            ...prev.customizations,
            [customizationId]: isSelected ? [optionId] : [],
          },
        };
      }
    });
  };

  // Check if restaurant is currently open
  const isRestaurantOpen = restaurant ? isRestaurantOpenAt(restaurant) : true;

  // Validate form before submission
  const isFormValid = () => {
    // Check if restaurant is closed
    if (!isRestaurantOpen) {
      return false;
    }

    // Check required customizations
    for (const customization of displayItem.customizations) {
      if (customization.is_required) {
        const selections = formData.customizations[customization.id] || [];
        if (selections.length === 0) {
          return false;
        }
      }
    }
    return formData.quantity > 0;
  };

  // Handle add to cart
  const handleAddToCart = () => {
    if (!isFormValid()) return;

    // Double-check restaurant is open before adding to cart
    if (!isRestaurantOpen) {
      return;
    }

    // Transform customizations to cart format
    const selectedCustomizations = Object.entries(formData.customizations)
      .filter(([_, optionIds]) => optionIds.length > 0)
      .map(([customizationId, optionIds]) => {
        const customization = displayItem.customizations.find(
          (c: any) => c.id === customizationId
        )!;
        const selectedOptions = optionIds.map((optionId) => {
          const option = customization.menu_customisation_items!.find(
            (opt: any) => opt.id === optionId
          )!;
          return {
            optionId,
            name: option.name,
            price: option.price,
          };
        });

        return {
          customizationId,
          name: customization.name,
          selectedOptions,
        };
      });

    // Create cart item
    const cartItem = {
      id: generateCartItemId(),
      menuItemId: displayItem.id,
      name: displayItem.name,
      price: displayItem.price,
      quantity: formData.quantity,
      customizations: selectedCustomizations,
      specialInstructions: formData.specialInstructions || undefined,
      totalPrice: calculateTotalPrice(),
    };

    // Set restaurant if provided
    if (restaurant) {
      actions.setRestaurant(restaurant);
    }

    actions.addItem(cartItem);

    // Show success toast
    const quantityText = formData.quantity > 1 ? `${formData.quantity}x ` : '';
    success(`${quantityText}${displayItem.name} added to cart`);

    onClose();
  };

  const totalPrice = calculateTotalPrice();

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Modal */}
          <motion.div
            ref={modalRef}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative bg-mina-surface rounded-mina shadow-mina max-w-2xl w-full max-h-[90vh] flex flex-col mx-4 sm:mx-0"
            onKeyDown={handleKeyDown}
          >
            {/* Header */}
            <div className="flex items-start justify-between p-4 sm:p-6 border-b border-mina-border flex-shrink-0">
              <div className="flex-1 pr-4">
                <h2
                  id="modal-title"
                  className="text-lg sm:text-xl font-semibold text-mina-espresso leading-tight"
                >
                  {displayItem.name}
                </h2>
                {item.description && (
                  <p id="modal-description" className="text-mina-muted text-sm mt-1 line-clamp-2">
                    {displayItem.description}
                  </p>
                )}
                <p
                  className="text-lg font-bold text-primary mt-2"
                  aria-label={`Price: $${displayItem.price.toFixed(2)}`}
                >
                  ${displayItem.price.toFixed(2)}
                </p>
              </div>

              <button
                ref={closeButtonRef}
                onClick={onClose}
                className="p-2 hover:bg-mina-sand rounded-full transition-colors touch-manipulation"
                aria-label="Close modal"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className="overflow-y-auto flex-1 min-h-0">
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                {/* Item Image */}
                {displayItem.image && (
                  <div className="w-full h-48 rounded-mina overflow-hidden">
                    <img
                      src={displayItem.image}
                      alt={`Image of ${displayItem.name}`}
                      className="mina-food-media"
                    />
                  </div>
                )}

                {/* Loading state for customizations */}
                {isLoadingCustomizations && (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mina-orange"></div>
                    <span className="ml-3 text-mina-muted">Loading customizations...</span>
                  </div>
                )}

                {/* Customizations */}
                {!isLoadingCustomizations &&
                  displayItem.customizations.map((customization) => (
                    <fieldset key={customization.id} className="space-y-3">
                      <legend className="flex flex-col gap-0.5">
                        <span className="font-semibold text-mina-espresso">
                          {customization.name}
                          {customization.is_required && (
                            <span className="text-mina-chili ml-1" aria-label="required">
                              *
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-mina-muted">
                          {customization.is_multichoice
                            ? customization.max_selection_number
                              ? `Select up to ${customization.max_selection_number}`
                              : 'Select multiple'
                            : 'Select one'}
                        </span>
                      </legend>

                      {customization.description && (
                        <p className="text-sm text-mina-muted">{customization.description}</p>
                      )}

                      <div className="space-y-2">
                        {customization.menu_customisation_items?.map((option: any) => {
                          const isSelected =
                            formData.customizations[customization.id]?.includes(option.id) || false;

                          return (
                            <label
                              key={option.id}
                              className={`
                              flex items-start sm:items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors touch-manipulation
                              ${
                                isSelected
                                  ? 'border-mina-orange bg-mina-orange/5'
                                  : 'border-mina-border hover:border-mina-orange/30 hover:bg-mina-offwhite'
                              }
                            `}
                            >
                              <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
                                <input
                                  type={customization.is_multichoice ? 'checkbox' : 'radio'}
                                  name={`customization-${customization.id}`}
                                  checked={isSelected}
                                  onChange={(e) =>
                                    handleCustomizationChange(
                                      customization.id,
                                      option.id,
                                      e.target.checked
                                    )
                                  }
                                  className="w-4 h-4 text-mina-mustard border-mina-border rounded focus:ring-mina-mustard mt-0.5 sm:mt-0 flex-shrink-0"
                                />
                                <div className="min-w-0 flex-1">
                                  <span className="font-medium text-sm sm:text-base text-mina-ink block">
                                    {option.name}
                                  </span>
                                  {option.description && (
                                    <p className="text-xs sm:text-sm text-mina-muted mt-1 line-clamp-2">
                                      {option.description}
                                    </p>
                                  )}
                                </div>
                              </div>

                              {option.price > 0 && (
                                <span className="mina-price text-sm sm:text-base ml-2 flex-shrink-0">
                                  +${option.price.toFixed(2)}
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </fieldset>
                  ))}

                {/* Special Instructions */}
                <div className="space-y-3">
                  <label
                    htmlFor="special-instructions"
                    className="font-semibold text-mina-espresso"
                  >
                    Special Instructions
                  </label>
                  <textarea
                    id="special-instructions"
                    value={formData.specialInstructions}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, specialInstructions: e.target.value }))
                    }
                    placeholder="Any special requests or notes for the chef..."
                    className="mina-textarea"
                    rows={3}
                    maxLength={200}
                    aria-describedby="instructions-help"
                  />
                  <p id="instructions-help" className="text-xs text-mina-muted">
                    {(formData.specialInstructions || '').length}/200 characters
                  </p>
                </div>
              </div>
            </div>

            {/* Footer - Fixed at bottom */}
            <div className="border-t border-mina-border p-4 sm:p-6 flex-shrink-0 bg-mina-offwhite">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                {/* Quantity Selector */}
                <div className="flex items-center gap-3">
                  <label
                    htmlFor="quantity-display"
                    className="font-medium text-sm sm:text-base text-mina-ink"
                  >
                    Quantity:
                  </label>
                  <div
                    className="flex items-center gap-2"
                    role="group"
                    aria-labelledby="quantity-label"
                  >
                    <button
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          quantity: Math.max(1, prev.quantity - 1),
                        }))
                      }
                      className="p-2 hover:bg-mina-sand rounded-full transition-colors touch-manipulation disabled:opacity-50"
                      disabled={formData.quantity <= 1}
                      aria-label="Decrease quantity"
                      type="button"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 12H4"
                        />
                      </svg>
                    </button>

                    <span
                      id="quantity-display"
                      className="w-8 text-center font-medium text-lg text-mina-ink"
                      aria-live="polite"
                      aria-label={`Quantity: ${formData.quantity}`}
                    >
                      {formData.quantity}
                    </span>

                    <button
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          quantity: prev.quantity + 1,
                        }))
                      }
                      className="p-2 hover:bg-mina-sand rounded-full transition-colors touch-manipulation"
                      aria-label="Increase quantity"
                      type="button"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Total Price */}
                <div className="text-center sm:text-right">
                  <p className="text-sm text-neutral-600">Total</p>
                  <p
                    className="text-xl font-bold text-primary"
                    aria-live="polite"
                    aria-label={`Total price: $${totalPrice.toFixed(2)}`}
                  >
                    ${totalPrice.toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Restaurant Closed Warning */}
              {!isRestaurantOpen && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <svg
                    className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-red-800">Restaurant Closed</p>
                    <p className="text-xs text-red-700 mt-1">
                      This restaurant is currently closed and not accepting orders.
                    </p>
                  </div>
                </div>
              )}

              {/* Add to Cart Button */}
              <button
                onClick={handleAddToCart}
                disabled={!isFormValid()}
                type="button"
                aria-describedby={!isFormValid() ? 'form-error' : undefined}
                className={`
                  w-full py-3 px-6 rounded-lg font-medium transition-colors touch-manipulation text-base sm:text-lg
                  ${
                    isFormValid()
                      ? 'mina-btn-primary'
                      : 'bg-mina-border text-mina-muted cursor-not-allowed'
                  }
                `}
              >
                {!isRestaurantOpen
                  ? 'Restaurant Closed'
                  : `Add to Cart - $${totalPrice.toFixed(2)}`}
              </button>

              {!isFormValid() && isRestaurantOpen && (
                <p
                  id="form-error"
                  className="text-sm text-mina-chili mt-2 text-center"
                  role="alert"
                >
                  Please complete all required selections
                </p>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default MenuItemModal;
